import { useEffect, useRef, useState } from "react";
import { EventSourcePolyfill } from "event-source-polyfill";
import {getLocalStorageItem, getSessionStorageItem} from "e3-common/utils/localStorageUtil";
import { LOCAL_STORAGE_KEYS } from "e3-common/constants/enumerates";
import { useFetch } from "e3-common/index";
import { completeServerUrl } from "e3-common/utils/urlUtil";

const useSse = (clientId, handlers = {}, reconnectInterval = 5000, maxReconnectAttempts = 10) => {
  const eventSourceRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const [sseToken, setSseToken] = useState(null);
  const { request } = useFetch();

  const API_SERVER_URL = completeServerUrl(import.meta.env.VITE_API_SERVER_URL) + "/api";

  const setupEventSource = async () => {
    try {
      const accessToken = getSessionStorageItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
      if (!accessToken) {
        console.error("Access Token이 없습니다.");
        return;
      }

      const tokenResponse = await request("/sse/getToken", {
        method: "POST",
        body: { clientId: clientId }
      }, { showLoad: false });

      const { sseToken: token } = tokenResponse;
      setSseToken(token);

      const eventSource = new EventSourcePolyfill(`${API_SERVER_URL}/sse/subscribe/${token}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        heartbeatTimeout: 60000,
      });

      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log("SSE 연결이 성공했습니다.");
        reconnectAttemptsRef.current = 0;
      };

      eventSource.onmessage = handlers.onMessage || ((event) => {
        console.log("기본 메시지 수신:", event.data);
      });

      eventSource.onerror = handlers.onError || ((error) => {
        console.error("SSE 에러가 발생했습니다:", error);
        eventSource.close();

        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectTimerRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            console.log(`SSE 재연결 시도중... (${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
            setupEventSource();
          }, reconnectInterval);
        } else {
          console.error("최대 재연결 횟수를 초과했습니다. 관리자에게 문의해주세요.");
        }
      });

    } catch (error) {
      console.error("SSE 연결 실패:", error);
    }
  };

  useEffect(() => {
    setupEventSource();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
    };
  }, []);

  return { eventSourceRef, sseToken };
};

export default useSse;